import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_KEY = 'flotte_cookie_consent'
const LEGACY_STORAGE_KEY = 'flotte:cookie-consent'

const DEFAULT_PREFERENCES = {
  essential: true,
  analytics: false,
  marketing: false,
}

function normalizePreferences(source) {
  return {
    essential: true,
    analytics: Boolean(source?.analytics),
    marketing: Boolean(source?.marketing),
  }
}

export function useCookieConsent(options = {}) {
  const { onConsentUpdated } = options
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES)
  const [showBanner, setShowBanner] = useState(false)
  const callbackRef = useRef(onConsentUpdated)

  useEffect(() => {
    callbackRef.current = onConsentUpdated
  }, [onConsentUpdated])

  const emitConsentUpdate = useCallback(nextPreferences => {
    if (typeof callbackRef.current === 'function') {
      callbackRef.current(nextPreferences)
    }
  }, [])

  const persistConsent = useCallback((payload, status) => {
    if (typeof window === 'undefined') return
    const record = {
      status,
      updatedAt: new Date().toISOString(),
      preferences: payload,
    }
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record))
      if (STORAGE_KEY !== LEGACY_STORAGE_KEY) {
        window.localStorage.removeItem(LEGACY_STORAGE_KEY)
      }
    } catch (error) {
      console.warn('No se pudo guardar el consentimiento de cookies.', error)
    }
  }, [])

  const applyConsent = useCallback(
    (nextPrefs, status) => {
      const normalized = normalizePreferences(nextPrefs)
      setPreferences(normalized)
      setShowBanner(false)
      persistConsent(normalized, status)
      emitConsentUpdate(normalized)
    },
    [emitConsentUpdate, persistConsent]
  )

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const stored =
        window.localStorage.getItem(STORAGE_KEY) ||
        window.localStorage.getItem(LEGACY_STORAGE_KEY)
      if (!stored) {
        setShowBanner(true)
        return
      }
      const parsed = JSON.parse(stored)
      const normalized = normalizePreferences(parsed?.preferences)
      setPreferences(normalized)
      setShowBanner(false)
      emitConsentUpdate(normalized)
    } catch (error) {
      console.warn('No se pudo leer el consentimiento de cookies.', error)
      setShowBanner(true)
    }
  }, [emitConsentUpdate])

  const acceptAll = useCallback(() => {
    applyConsent(
      {
        essential: true,
        analytics: true,
        marketing: true,
      },
      'accepted_all'
    )
  }, [applyConsent])

  const rejectAll = useCallback(() => {
    applyConsent(
      {
        essential: true,
        analytics: false,
        marketing: false,
      },
      'rejected_all'
    )
  }, [applyConsent])

  const saveCustomConsent = useCallback(
    nextPreferences => {
      applyConsent(
        {
          essential: true,
          analytics: Boolean(nextPreferences?.analytics),
          marketing: Boolean(nextPreferences?.marketing),
        },
        'custom'
      )
    },
    [applyConsent]
  )

  const resetConsent = useCallback(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.warn('No se pudo limpiar el consentimiento de cookies.', error)
    }
    setPreferences(DEFAULT_PREFERENCES)
    setShowBanner(true)
  }, [])

  return {
    showBanner,
    preferences,
    acceptAll,
    rejectAll,
    saveCustomConsent,
    resetConsent,
  }
}
